import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CATEGORIES } from '../mock/mock';
import LawFirmCard from '../components/LawFirmCard';
import SEO, { SITE_NAME } from '../components/SEO';
import { firmsApi, metaApi } from '../api/client';

const selectCls = 'aj-input appearance-none bg-[url("data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2216%22 height=%2216%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22%23153e44%22 stroke-width=%222%22><path d=%22m6 9 6 6 6-6%22/></svg>")] bg-no-repeat bg-[right_14px_center] pr-10';

export default function Home() {
  const navigate = useNavigate();
  const [city, setCity] = useState('');
  const [category, setCategory] = useState('');
  const [q, setQ] = useState('');
  const [featured, setFeatured] = useState([]);
  const [cities, setCities] = useState([]);
  const [categories, setCategories] = useState(CATEGORIES);
  const [stats, setStats] = useState({ firms: '—', cities: '—', categories: CATEGORIES.length, articles: '—' });

  useEffect(() => {
    firmsApi.list({ sort: 'featured', limit: 8 }).then(d => setFeatured(d.items)).catch(() => {});
    metaApi.cities().then(setCities).catch(() => {});
    metaApi.categories().then(d => Array.isArray(d) && d.length && setCategories(d)).catch(() => {});
    metaApi.stats().then(setStats).catch(() => {});
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (city) params.set('city', city);
    if (category) params.set('cat', category);
    navigate(`/advogados?${params.toString()}`);
  };

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: origin,
    potentialAction: { '@type': 'SearchAction', target: `${origin}/advogados?q={search_term_string}`, 'query-input': 'required name=search_term_string' },
  };

  const activeCities = cities.filter(c => c.count > 0);

  return (
    <main className="aj-wrap" id="aj-main">
      <SEO
        title="Encontre o advogado certo para a sua causa"
        description="Ambito Advogados, a plataforma de advogados do Âmbito Jurídico. Perfis reais de escritórios de advocacia por cidade, área de atuação, equipe e conteúdo publicado."
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="grid grid-cols-1 md:grid-cols-[1.15fr_1fr] items-center gap-8 md:gap-16 py-9 md:py-[62px]" aria-labelledby="home-title">
        <div>
          <span className="aj-kicker">Direito para entender. Advogados para resolver.</span>
          <h1 id="home-title" className="text-[32px] sm:text-[38px] lg:text-[50px] mt-4 mb-5" data-testid="home-title">
            Encontre o advogado certo para o seu caso.
          </h1>
          <p className="text-[18px] md:text-[20px] text-[#354c54] max-w-[520px] mb-6" data-testid="home-subtitle">
            Resolva seu problema com um advogado bem avaliado. Encontre advogado por especialidade, por cidade ou que atua no Brasil inteiro online.
          </p>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            <Link to="/advogados" className="aj-button aj-primary w-full sm:w-auto" data-testid="home-browse-cta">Ver todos os escritórios <span aria-hidden="true">→</span></Link>
            <Link to="/match" className="aj-link text-[16px] py-3 inline-block" data-testid="home-match-cta">Busca Advogado com IA <span aria-hidden="true">→</span></Link>
          </div>
        </div>

        <div className="aj-box" id="aj-search">
          <h2 className="text-[25px] mt-0 mb-2">Qual é o seu problema?</h2>
          <p className="text-[17px] text-[#354c54] mb-4">Pesquise por área, cidade ou palavra-chave.</p>
          <form onSubmit={handleSearch} aria-label="Buscar escritórios de advocacia" data-testid="home-search-form">
            <label htmlFor="home-q" className="block mt-3 mb-2 text-[16px] font-semibold text-[#193b43]">Assunto ou palavra-chave</label>
            <input id="home-q" className="aj-input" value={q} onChange={e => setQ(e.target.value)} placeholder="Ex.: demissão sem justa causa" data-testid="home-search-input" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label htmlFor="home-city" className="block mt-3 mb-2 text-[16px] font-semibold text-[#193b43]">Cidade</label>
                <select id="home-city" className={selectCls} value={city} onChange={e => setCity(e.target.value)} data-testid="home-city-select">
                  <option value="">Todas as cidades</option>
                  {activeCities.map(c => <option key={c.slug} value={c.slug}>{c.name}/{c.state}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="home-cat" className="block mt-3 mb-2 text-[16px] font-semibold text-[#193b43]">Área do direito</label>
                <select id="home-cat" className={selectCls} value={category} onChange={e => setCategory(e.target.value)} data-testid="home-category-select">
                  <option value="">Todas as áreas</option>
                  {categories.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                </select>
              </div>
            </div>
            <button type="submit" className="aj-button aj-primary w-full mt-4" data-testid="home-search-submit">Buscar escritórios <span aria-hidden="true">→</span></button>
          </form>
          <p className="aj-small mt-6 mb-2.5">Explore por área</p>
          <div className="flex flex-wrap gap-2.5">
            {categories.slice(0, 6).map(c => (
              <Link key={c.slug} to={`/categoria/${c.slug}`} className="aj-chip">{c.name}</Link>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="aj-section !py-7" aria-label="Números da plataforma" data-testid="home-stats">
        <dl className="grid grid-cols-2 md:grid-cols-4 gap-6 m-0">
          {[
            { val: stats.firms, label: 'Escritórios' },
            { val: stats.cities, label: 'Cidades' },
            { val: stats.categories, label: 'Áreas do direito' },
            { val: stats.articles, label: 'Artigos jurídicos' },
          ].map(s => (
            <div key={s.label}>
              <dt className="aj-kicker">{s.label}</dt>
              <dd className="m-0 text-[28px] md:text-[32px] font-semibold text-[#122f36] leading-tight">{s.val}</dd>
            </div>
          ))}
        </dl>
      </section>

      {/* Áreas */}
      <section className="aj-section" aria-labelledby="home-areas-title">
        <div className="aj-section-heading">
          <div><span className="aj-kicker">Áreas de atuação</span><h2 id="home-areas-title" className="text-[27px] md:text-[35px] mt-2 mb-0">Encontre por área do direito.</h2></div>
          <Link to="/categorias" className="aj-link hidden md:inline-block py-3 shrink-0">Ver todas as áreas <span aria-hidden="true">→</span></Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8">
          {categories.map(cat => (
            <article key={cat.slug} className="aj-story">
              <h3 className="text-[20px] leading-[1.4] m-0">
                <Link to={`/categoria/${cat.slug}`} className="text-[#122f36] hover:text-[#063f4c] underline underline-offset-[0.18em] decoration-[#cbd8dc] hover:decoration-[#063f4c]">{cat.name}</Link>
              </h3>
              {cat.count !== undefined && <span className="text-[15px] text-[#52676e] mt-1">{cat.count} {cat.count === 1 ? 'escritório' : 'escritórios'}</span>}
            </article>
          ))}
        </div>
        <Link to="/categorias" className="aj-link md:hidden inline-block py-3 mt-2">Ver todas as áreas <span aria-hidden="true">→</span></Link>
      </section>

      {/* Destaques */}
      {featured.length > 0 && (
        <section className="aj-section" aria-labelledby="home-featured-title">
          <div className="aj-section-heading">
            <div><span className="aj-kicker">Perfis reais</span><h2 id="home-featured-title" className="text-[27px] md:text-[35px] mt-2 mb-0">Escritórios em destaque.</h2></div>
            <Link to="/advogados" className="aj-link hidden md:inline-block py-3 shrink-0">Ver todos <span aria-hidden="true">→</span></Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-4" data-testid="home-featured-grid">
            {featured.map(f => <LawFirmCard key={f.id} firm={f} />)}
          </div>
        </section>
      )}

      {/* Cidades */}
      <section className="aj-section" aria-labelledby="home-cities-title">
        <div className="aj-section-heading">
          <div><span className="aj-kicker">Perto de você</span><h2 id="home-cities-title" className="text-[27px] md:text-[35px] mt-2 mb-0">Advogados por cidade.</h2></div>
          <Link to="/cidades" className="aj-link hidden md:inline-block py-3 shrink-0">Ver todas as cidades <span aria-hidden="true">→</span></Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8">
          {activeCities.slice(0, 12).map(c => (
            <article key={c.slug} className="aj-story">
              <h3 className="text-[20px] leading-[1.4] m-0">
                <Link to={`/cidade/${c.slug}`} className="text-[#122f36] hover:text-[#063f4c] underline underline-offset-[0.18em] decoration-[#cbd8dc] hover:decoration-[#063f4c]">Advogados em {c.name}/{c.state}</Link>
              </h3>
              <span className="text-[15px] text-[#52676e] mt-1">{c.count} {c.count === 1 ? 'escritório' : 'escritórios'}</span>
            </article>
          ))}
        </div>
      </section>

      {/* Ferramentas */}
      <section className="aj-section grid grid-cols-1 md:grid-cols-[1.15fr_1fr] gap-8 md:gap-16 items-center" aria-labelledby="home-tools-title">
        <div>
          <span className="aj-kicker">Ferramentas gratuitas</span>
          <h2 id="home-tools-title" className="text-[27px] md:text-[35px] mt-2">Entenda seus direitos<br />antes de contratar.</h2>
          <p className="text-[#354c54] mb-5">Simule verbas rescisórias e regras de aposentadoria com base na CLT e na Reforma da Previdência. Sem cadastro.</p>
          <Link to="/match" className="aj-button">Busca Advogado com IA <span aria-hidden="true">→</span></Link>
        </div>
        <div className="aj-edition">
          <span className="aj-kicker">Simuladores</span>
          <h3 className="text-[23px] leading-[1.4] mt-2"><Link to="/simulador/rescisao" className="aj-link font-semibold" data-testid="home-rescisao-cta">Foi demitido? Simule sua rescisão em 30 segundos</Link></h3>
          <p className="aj-small mb-4">Aviso prévio, 13º, férias, multa do FGTS e todas as verbas rescisórias.</p>
          <h3 className="text-[23px] leading-[1.4]"><Link to="/simulador/aposentadoria" className="aj-link font-semibold" data-testid="home-aposentadoria-cta">Quando você pode se aposentar?</Link></h3>
          <p className="aj-small mb-0">Tempo mínimo, regras de transição e estimativa do benefício INSS.</p>
        </div>
      </section>

      {/* Para advogados */}
      <section className="aj-section grid grid-cols-1 md:grid-cols-[1.15fr_1fr] gap-8 md:gap-16 items-center" aria-labelledby="home-lawyers-title">
        <div>
          <span className="aj-kicker">Para advogados e escritórios</span>
          <h2 id="home-lawyers-title" className="text-[27px] md:text-[35px] mt-2">Seu escritório ganha<br />visibilidade no Google.</h2>
          <p className="text-[#354c54] max-w-[590px] mb-0">Perfil otimizado para SEO, contato direto via WhatsApp, publicação de artigos, avaliações de clientes e recepcionista com IA. Sem venda de leads, sem comissão.</p>
        </div>
        <div>
          <Link to="/cadastro" className="aj-button aj-primary" data-testid="home-register-cta">Cadastrar meu escritório <span aria-hidden="true">→</span></Link>
          <p className="aj-small mt-4 max-w-[370px]">Perfil gratuito. Planos pagos opcionais, em conformidade com o Provimento nº 205/2021 da OAB. <Link to="/planos" className="aj-link font-normal">Ver planos</Link>.</p>
        </div>
      </section>
    </main>
  );
}
